import { SemVer } from "semver";

import * as envService from "../services/env";
import * as depsService from "../services/deps";

export type DepType = "single" | "flex";

export type AppDep = {
  type?: DepType;
  versionString: string;
  version: SemVer;
  isInAppPackageJson?: boolean;
  isUsingProvided?: boolean;
  isThoughtToBeProvided?: boolean;
};

export type SharedFabricDep = {
  type: DepType;
  versionString: string;
  version: SemVer;
};

export type AppDepsByName = Record<string, AppDep>;
export type SharedFabricDeps = Record<string, SharedFabricDep>;

export const addToAppDeps = (
  appEnv: envService.Env,
  appDeps: AppDepsByName,
  depName: string,
  isOpenToUsingProvided: boolean,
): void => {
  const appActualDepVersions = envService.getAppActualDepVersions(appEnv);
  const appPackageJsonDeps = envService.getAppPackageJsonDeps(appEnv);
  const appPackageJsonDevDeps = envService.getAppPackageJsonDevDeps(appEnv);
  const appFabricCoreSharedSingleDepNames =
    envService.getAppFabricCoreSharedSingleDepNames(appEnv);
  const appFabricCoreSharedFlexDepNames =
    envService.getAppFabricCoreSharedFlexDepNames(appEnv);
  const appFabricCoreAllSharedDepNames =
    envService.getAppFabricCoreAllSharedDepNames(appEnv);

  const version = depsService.coerceVersion(
    appActualDepVersions[depName],
    depName,
  );
  const versionString = version.version;
  const isInAppPackageJson =
    depName in appPackageJsonDeps || depName in appPackageJsonDevDeps;
  const isUsingProvided = isOpenToUsingProvided
    ? appFabricCoreAllSharedDepNames.includes(depName)
    : false;
  const isThoughtToBeProvided =
    appFabricCoreAllSharedDepNames.includes(depName);
  const isThoughtToBeProvidedSingleDep =
    isThoughtToBeProvided &&
    appFabricCoreSharedSingleDepNames.includes(depName);
  const isThoughtToBeProvidedFlexDep =
    isThoughtToBeProvided && appFabricCoreSharedFlexDepNames.includes(depName);

  let type: AppDep["type"] = undefined;
  if (isThoughtToBeProvidedSingleDep) {
    type = "single";
  } else if (isThoughtToBeProvidedFlexDep) {
    type = "flex";
  }

  appDeps[depName] = { version, versionString };
  if (isInAppPackageJson) {
    appDeps[depName].isInAppPackageJson = true;
  }
  if (isUsingProvided) {
    appDeps[depName].isUsingProvided = true;
  }
  if (isThoughtToBeProvided) {
    appDeps[depName].isThoughtToBeProvided = true;
  }
  if (type) {
    appDeps[depName].type = type;
  }
};

export const getDepVersionInSharedFabricCorePackageJson = (
  depName: string,
): string | undefined => {
  const sharedFabricCorePackageJsonVersion =
    envService.getSharedFabricCorePackageJsonVersion();
  const sharedFabricCorePackageJsonDeps =
    envService.getSharedFabricCorePackageJsonDeps();
  return depName === "@granular/fabric-core"
    ? sharedFabricCorePackageJsonVersion
    : sharedFabricCorePackageJsonDeps[depName];
};

export const addToSharedFabricDeps = (
  sharedFabricDeps: SharedFabricDeps,
  depName: string,
  depType: DepType,
): void => {
  const actualDepVersionsOfContainer = envService.getAppActualDepVersions(
    process.env,
  );
  const actualDepVersionInContainer = actualDepVersionsOfContainer[depName];
  const coercedActualDepVersionInContainer = depsService.coerceVersion(
    actualDepVersionInContainer,
    depName,
  );

  let depVersionInSharedFabricCorePackageJson =
    getDepVersionInSharedFabricCorePackageJson(depName);

  // Effectively skip any packages referencing local file paths by forcing a version match.
  // This is to allow for local package development.
  const shouldSkip =
    depVersionInSharedFabricCorePackageJson?.startsWith("file:");
  if (shouldSkip) {
    depVersionInSharedFabricCorePackageJson = actualDepVersionInContainer;
  }

  // If the dependency is listed in the Fabric Core package.json
  // and the version doesn't match the installed version in Fabric Container
  // then throw as these need to match.
  if (depVersionInSharedFabricCorePackageJson) {
    const coercedDepVersionInSharedFabricCorePackageJson =
      depsService.coerceVersion(depVersionInSharedFabricCorePackageJson);
    if (
      coercedDepVersionInSharedFabricCorePackageJson.version !==
      coercedActualDepVersionInContainer.version
    ) {
      throw new Error(
        [
          `Version mismatch for "${depName}".`,
          `Fabric Core expects "${depVersionInSharedFabricCorePackageJson}"`,
          `but Fabric Container has "${actualDepVersionInContainer}" installed.`,
        ].join(" "),
      );
    }
  }

  sharedFabricDeps[depName] = {
    type: depType,
    versionString: coercedActualDepVersionInContainer.version,
    version: coercedActualDepVersionInContainer,
  };
};
